/* eslint-disable arrow-body-style */

import React, { useState } from 'react';
import classnames from 'classnames';
import { history } from 'umi';
import { Layout, Tabs, Tab, TabPanel, Card } from 'tea-component';
import { Bubble } from '@home/components/TeaComponents';
import RecordableSearchBox from '@gp/components/RecordableSearchBox';
import ProjectMetricsTable from './components/ProjectMetricsTable';
import StudioFinancialMetricsTable from './components/StudioFinancialMetricsTable';
import CommonSystemTerminologyTable from './components/CommonSystemTerminologyTable';
import useRequest from '@gp/hooks/useRequest';
import usePageLog from '@gp/hooks/usePageLog';
import useMetricsUpdateQueryParams from './_hooks/useMetricsUpdateQueryParams';
import useMetricsSearch from './_hooks/useMetricsSearch';
import { fetchMetrics } from '@gp/services/metrics';
import { getMatchedActiveId, resetPageScrollTop, getMetricsDataSafety } from './utils';
import { HISTORY_PAGE_TYPES } from '@gp/constants/const';
import { SEARCH_SELECT_COMP_AUTO_CLOSE_WHEN_SCROLL_NODE } from '@gp/constants/classNames';
import {
  METRICS_DEFAULT_ACTIVE_ID,
  METRICS_DEFAULT_ACTIVE_IDS,
  DISABLED_TAB_TIPS_WHEN_NO_SEARCH_RESULT,
} from './constants';
import {
  PROJECT_METRICS_TAB_CONFIG,
  STUDIO_FINANCIAL_METRICS_TAB_CONFIG,
  COMMON_SYSTEM_TERMINOLOGY_TAB_CONFIG,
} from './constants/config';

import type { IRecord } from '@gp/types/global';
import type { IMetricsCategory, ICategoryMetricsData } from '@gp/types/metrics';
import type { IMetricsTab } from './types';

import './index.scss';

const { Content } = Layout;

const getDisabledTabLabel = (
  disabled: boolean,
  title: string,
  disabledTips = DISABLED_TAB_TIPS_WHEN_NO_SEARCH_RESULT,
) => {
  if (!disabled) return title;

  return (
    <Bubble className="dbg-bubble-max-width" dark arrowPointAtCenter placement="right" content={disabledTips}>
      <span>{title}</span>
    </Bubble>
  );
};

const Metrics: React.FC = () => {
  const { tab: initialActiveId } = history.location.query as IRecord;

  const {
    loading,
    error,
    data: metricsRes,
  } = useRequest(fetchMetrics, {
    onFinally: (_params, metricsRes) => {
      setMetricsDataSafety(metricsRes?.data);
    },
  });

  const [metricsData, setMetricsData] = useState<ICategoryMetricsData>({
    project_metrics: [],
    studio_financial_metrics: [],
    common_system_terminology: [],
  });

  const setMetricsDataSafety = (payload?: ICategoryMetricsData | null) => {
    setMetricsData(getMetricsDataSafety(payload));
  };

  const [availableIds, setAvailableIds] = useState<IMetricsCategory[]>(METRICS_DEFAULT_ACTIVE_IDS);
  const [activeId, setActiveId] = useState<IMetricsCategory>(() => getMatchedActiveId(tabs, initialActiveId));

  const isProjectMetricsDisabled = !availableIds?.includes(PROJECT_METRICS_TAB_CONFIG.key);
  const isStudioFinancialMetricsDisabled = !availableIds?.includes(STUDIO_FINANCIAL_METRICS_TAB_CONFIG.key);
  const isCommonSystemTerminologyDisabled = !availableIds?.includes(COMMON_SYSTEM_TERMINOLOGY_TAB_CONFIG.key);

  const tabs: IMetricsTab[] = [
    {
      id: PROJECT_METRICS_TAB_CONFIG.key,
      label: getDisabledTabLabel(isProjectMetricsDisabled, PROJECT_METRICS_TAB_CONFIG.title),
      disabled: isProjectMetricsDisabled,
      component: <ProjectMetricsTable />,
    },
    {
      id: STUDIO_FINANCIAL_METRICS_TAB_CONFIG.key,
      label: getDisabledTabLabel(isStudioFinancialMetricsDisabled, STUDIO_FINANCIAL_METRICS_TAB_CONFIG.title),
      disabled: isStudioFinancialMetricsDisabled,
      component: <StudioFinancialMetricsTable />,
    },
    {
      id: COMMON_SYSTEM_TERMINOLOGY_TAB_CONFIG.key,
      label: getDisabledTabLabel(isCommonSystemTerminologyDisabled, COMMON_SYSTEM_TERMINOLOGY_TAB_CONFIG.title),
      disabled: isCommonSystemTerminologyDisabled,
      component: <CommonSystemTerminologyTable />,
    },
  ];

  const tab = tabs?.find((tab) => tab?.id === activeId) as IMetricsTab;

  const handleActive = (tab: Tab) => {
    const activeIdTmp = (tab?.id as IMetricsCategory) || METRICS_DEFAULT_ACTIVE_ID;
    resetPageScrollTop();
    setActiveId(activeIdTmp);
  };

  const handleRetry = () => {
    onKeywordClear();
  };

  const handleReset = () => {
    onKeywordClear();
  };

  usePageLog({ pageId: 'METRICS' });

  useMetricsUpdateQueryParams(activeId);

  const {
    keyword,
    // onKeywordChange,
    onKeywordSearch,
    onKeywordClear,
  } = useMetricsSearch({
    fullMetricsData: metricsRes?.data,
    activeId,
    setActiveId,
    setAvailableIds,
    setMetricsData: setMetricsDataSafety,
  });

  return (
    <Content className="dbg-initial-line-height dbg-tab-page dbg-metrics-page">
      <Content.Header className="dbg-tab-page__header dbg-metrics-page__header" title="Metrics">
        <RecordableSearchBox
          domain={HISTORY_PAGE_TYPES.METRICS}
          searchLogKey="METRICS_SEARCH"
          deleteSearchBtnLogKey="METRICS_DELETE_SEARCH_SUBMIT"
          dropdownProps={{ className: 'dbg-metrics-page__search-box-dropdown' }}
          searchBoxProps={{
            style: { width: 420 },
            placeholder: 'Search by metric',
            size: 'l',
            maxLength: 50,
          }}
          value={keyword}
          // onChange={onKeywordChange}
          onSearch={onKeywordSearch}
          onClear={onKeywordClear}
        />
        <Tabs
          className="dbg-tab-page__tabs dbg-metrics-page__tabs"
          ceiling
          tabs={tabs}
          activeId={!availableIds?.length ? '' : activeId}
          onActive={handleActive}
        />
      </Content.Header>
      <Content.Body
        full
        className={classnames(
          'dbg-tab-page__body dbg-metrics-page__body',
          SEARCH_SELECT_COMP_AUTO_CLOSE_WHEN_SCROLL_NODE,
        )}
      >
        <TabPanel key={tab?.id} id={tab?.id}>
          <Card className="dbg-tab-page__card">
            <Card.Body className="dbg-tab-page__card-body">
              {React.cloneElement(tab?.component, {
                loading,
                error: !!error,
                noSearchResult: !availableIds?.length,
                data: loading ? [] : metricsData?.[tab?.id as keyof ICategoryMetricsData],
                keyword,
                onRetry: handleRetry,
                onReset: handleReset,
              })}
            </Card.Body>
          </Card>
        </TabPanel>
      </Content.Body>
    </Content>
  );
};

export default Metrics;
